// The candidate's exam journal: what happens on this screen that the exam server cannot see - going offline and
// back, the page being reloaded or hidden, error messages shown, failed requests, an option chosen before Save.
// The server keeps it in its audit log with everything else about the exam (backend utils/audit-log.js).
//
// Each entry is kept in this browser first (IndexedDB, which survives a reload or a closed browser), numbered per
// login session. Entries go to the server riding on requests the exam makes anyway (timer, answer saves, submit),
// so the journal adds no requests; only a backlog is sent on its own (after being offline, or left over from an
// earlier login on this computer, with that login's own token). The server answers up to which number it kept
// them (header X-Journal-Ack) and only those are deleted here. Nothing here can stop or slow the exam: every
// failure is ignored.

import { getStoredAuth, getStoredSubject } from "./authStorage";

const DB_NAME = "exam-journal";
const STORE = "entries";
const META = "sessions";
const PIGGYBACK_MAX = 100;
const UPLOAD_MAX = 300;
const FLUSH_MS = 30 * 1000;
// The current session's entries normally go with the next timer request (every minute); only ones that have
// waited longer than this are sent on their own
const STALE_MS = 90 * 1000;

// Requests that carry the journal (the server takes it out before anything else looks at the request)
export const CARRIES_JOURNAL = [
    ["put", /\/exam\/time$/],
    ["put", /\/exam\/questions$/],
    ["post", /\/exam\/submit$/],
];

const sessionOf = (token) => {
    try {
        return JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).sid || null;
    } catch (e) {
        return null;
    }
};

let dbPromise = null;
const openDb = () => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve) => {
        try {
            const req = indexedDB.open(DB_NAME, 1);
            req.onupgradeneeded = () => {
                const db = req.result;
                const store = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
                store.createIndex("sid", "sid");
                db.createObjectStore(META, { keyPath: "sid" });
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(null);
        } catch (e) {
            resolve(null);
        }
    });
    return dbPromise;
};

// Without IndexedDB (a locked-down browser), entries are kept in memory: they still ride on requests
let memory = [];
let memorySeq = {};

const tx = async (stores, mode, fn) => {
    const db = await openDb();
    if (!db) return fn(null);
    return new Promise((resolve) => {
        try {
            const t = db.transaction(stores, mode);
            let result;
            Promise.resolve(fn(t)).then((r) => { result = r; });
            t.oncomplete = () => resolve(result);
            t.onerror = () => resolve(result);
            t.onabort = () => resolve(result);
        } catch (e) {
            resolve(undefined);
        }
    });
};
const req2p = (req) => new Promise((resolve) => { req.onsuccess = () => resolve(req.result); req.onerror = () => resolve(undefined); });

// Adds one entry for the logged-in candidate's current session. kind: UPPER_CASE; q: question, v: number, d: text.
export const log = (kind, { q = null, v = null, d = null } = {}) => {
    try {
        const token = getStoredAuth()?.token;
        const sid = sessionOf(token);
        if (!sid) return;
        const x = getStoredSubject()?.userCurrentSlotData?.[0]?.examId || null;
        const entry = { sid, token, t: Date.now(), k: kind, q, v: v == null ? null : Math.round(Number(v)), d: d == null ? null : String(d).slice(0, 255), x };
        tx([STORE, META], "readwrite", async (t) => {
            if (!t) {
                memorySeq[sid] = (memorySeq[sid] || 0) + 1;
                memory.push({ ...entry, s: memorySeq[sid] });
                return;
            }
            const meta = t.objectStore(META);
            const current = (await req2p(meta.get(sid))) || { sid, seq: 0 };
            current.seq += 1;
            meta.put(current);
            t.objectStore(STORE).add({ ...entry, s: current.seq });
        });
    } catch (e) {
        // never in the exam's way
    }
};

const pendingFor = async (sid, max) => {
    const all = await tx([STORE], "readonly", async (t) => {
        if (!t) return memory.filter((e) => e.sid === sid);
        return (await req2p(t.objectStore(STORE).index("sid").getAll(sid))) || [];
    });
    return (all || []).sort((a, b) => a.s - b.s).slice(0, max);
};

const shape = (entries) => ({
    n: Date.now(),
    entries: entries.map(({ s, t, k, q, v, d, x }) => ({ s, t, k, q, v, d, x })),
});

// The server kept this session's entries up to `seq`: forget them here
export const ack = (sid, seq) => {
    if (!sid || !(seq > 0)) return;
    tx([STORE], "readwrite", async (t) => {
        if (!t) {
            memory = memory.filter((e) => !(e.sid === sid && e.s <= seq));
            return;
        }
        const store = t.objectStore(STORE);
        const rows = (await req2p(store.index("sid").getAll(sid))) || [];
        rows.filter((r) => r.s <= seq).forEach((r) => store.delete(r.id));
    });
};

// For a request that carries the journal: this session's oldest unsent entries (or null)
export const take = async () => {
    try {
        const sid = sessionOf(getStoredAuth()?.token);
        if (!sid) return null;
        const entries = await pendingFor(sid, PIGGYBACK_MAX);
        return entries.length ? { sid, journal: shape(entries) } : null;
    } catch (e) {
        return null;
    }
};

export const carries = (method, url) => CARRIES_JOURNAL.some(([m, re]) => m === String(method || "").toLowerCase() && re.test(String(url || "")));

// Sends what has not gone with a request: every session's backlog, each with its own login's token. A token the
// server no longer accepts (expired) can never upload: those entries are dropped.
let flushing = false;
export const flush = async () => {
    if (flushing || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
    flushing = true;
    try {
        const all = await tx([STORE], "readonly", async (t) => (t ? (await req2p(t.objectStore(STORE).getAll())) || [] : memory.slice()));
        const sids = [...new Set((all || []).map((e) => e.sid))];
        const current = sessionOf(getStoredAuth()?.token);
        for (const sid of sids) {
            const entries = (all || []).filter((e) => e.sid === sid).sort((a, b) => a.s - b.s).slice(0, UPLOAD_MAX);
            if (sid === current && !(entries[0] && Date.now() - entries[0].t > STALE_MS)) continue;
            const token = entries[entries.length - 1]?.token;
            if (!entries.length || !token) continue;
            try {
                // Not through the exam's request helper: a refusal here must never log the candidate out
                const res = await fetch(`${process.env.REACT_APP_API_URL}/exam/journal`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ journal: shape(entries) }),
                });
                const kept = parseInt(res.headers.get("X-Journal-Ack"), 10);
                if (kept > 0) ack(sid, kept);
                else if (res.status === 401) ack(sid, entries[entries.length - 1].s);
            } catch (e) {
                // offline or server down: tried again later
            }
        }
    } finally {
        flushing = false;
    }
};

// Started once by the app: the backlog every FLUSH_MS and on coming back online; offline / hidden periods
let started = false;
export const start = () => {
    if (started || typeof window === "undefined") return;
    started = true;
    log("PAGE_LOAD", { d: window.location.pathname });
    let offlineSince = navigator.onLine === false ? Date.now() : null;
    window.addEventListener("offline", () => {
        offlineSince = Date.now();
        log("OFFLINE");
    });
    window.addEventListener("online", () => {
        log("ONLINE", { v: offlineSince ? (Date.now() - offlineSince) / 1000 : null });
        offlineSince = null;
        flush();
    });
    let hiddenSince = null;
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            hiddenSince = Date.now();
            log("HIDDEN");
        } else {
            log("VISIBLE", { v: hiddenSince ? (Date.now() - hiddenSince) / 1000 : null });
            hiddenSince = null;
        }
    });
    setTimeout(flush, 3000);
    setInterval(flush, FLUSH_MS);
};

export const journalSession = (token) => sessionOf(token);
